import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { useAuth } from "@/lib/auth";
import { Trash2, Pencil, Plus, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPosts() {
  const { authHeader } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/posts`, { headers: authHeader });
      setPosts(data);
    } catch {
      toast.error("Could not load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, { headers: authHeader });
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Post deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <AdminShell
      title="Posts"
      action={
        <Link
          to="/admin/posts/new"
          data-testid="admin-new-post"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] text-white rounded-full text-xs tracking-wide transition-colors"
        >
          <Plus className="w-4 h-4" /> New post
        </Link>
      }
    >
      <div data-testid="admin-posts-list" className="border border-[var(--border-dim)] rounded-sm overflow-hidden bg-[var(--bg-2)]">
        {loading && (
          <div className="px-5 py-10 text-center font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">
            Loading...
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div data-testid="admin-posts-empty" className="px-5 py-14 text-center">
            <FileText className="w-6 h-6 mx-auto text-[var(--text-3)] mb-3" />
            <div className="font-display text-lg">No posts yet</div>
            <div className="overline mt-2">Create your first insight piece.</div>
          </div>
        )}

        {!loading && posts.map((p) => (
          <div
            key={p.id}
            data-testid={`admin-post-row-${p.id}`}
            className="grid grid-cols-12 gap-4 px-5 py-4 items-center border-b border-[var(--border-dim)] last:border-b-0 hover:bg-[var(--surface)]/40 transition-colors"
          >
            <div className="col-span-6">
              <div className="font-display font-medium">{p.title}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-3)] mt-1">
                /blog/{p.slug}
              </div>
            </div>
            <div className="col-span-2">
              <span
                className={`font-mono-ui text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full border ${
                  p.published
                    ? "border-[var(--lime)]/40 text-[var(--lime)] bg-[var(--lime)]/5"
                    : "border-[var(--border-dim)] text-[var(--text-3)]"
                }`}
              >
                {p.published ? "Live" : "Draft"}
              </span>
            </div>
            <div className="col-span-3 font-mono-ui text-[11px] text-[var(--text-3)]">
              {new Date(p.created_at).toLocaleDateString()}
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <Link
                to={`/admin/posts/${p.id}/edit`}
                data-testid={`admin-post-edit-${p.id}`}
                className="text-[var(--text-3)] hover:text-[var(--lime)] p-1 transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </Link>
              <button
                data-testid={`admin-post-delete-${p.id}`}
                onClick={() => remove(p.id)}
                className="text-[var(--text-3)] hover:text-[var(--vermilion)] p-1 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
